import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'

import { toast } from 'react-toastify'
import { useForm, FormProvider } from 'react-hook-form'
import { Box, DialogContent, Button } from '@mui/material'

import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { useCreateInvoiceGroupMutation } from 'api/groups/invoice/createInvoiceGroup'
import { useUpdateInvoiceGroupMutation } from 'api/groups/invoice/updateInvoiceGroup'

import T from 'T'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'
import HHDialogActions from 'components/common/HHDialogActions'
import CancelButton from 'components/buttons/CancelButton'
import HHFormTextField from 'components/form-fields/v5/HHFormTextField'

const AddEditInvoiceGroupDialog = ({ isOpen = false, existingGroup = null, onSuccess = noop, onClose }) => {
  const [createInvoiceGroup, { isLoading: isCreateLoading }] = useCreateInvoiceGroupMutation()
  const [updateInvoiceGroup, { isLoading: isUpdateLoading }] = useUpdateInvoiceGroupMutation()
  const invoiceGroupForm = useForm({ defaultValues: { groupName: '' } })

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = invoiceGroupForm

  const groupNameHelperText = get(errors, 'groupName.message')

  const handleSuccess = (response, message) => {
    onSuccess(response.id)
    toast.success(message)
    onClose()
  }

  const onSubmitHandler = data => {
    const existingGroupId = get(existingGroup, 'id')
    const payload = { groupName: data?.groupName }
    if (existingGroupId) {
      // Edit group
      updateInvoiceGroup({ invoiceGroupId: existingGroupId, body: payload })
        .unwrap()
        .then(response => handleSuccess(response, T.INVOICE_GROUP_UPDATED_SUCCESS_MSG))
        .catch(handleError)
      return
    }

    // Add group
    createInvoiceGroup(payload)
      .unwrap()
      .then(response => handleSuccess(response, T.INVOICE_GROUP_CREATED_SUCCESS_MSG))
      .catch(handleError)
  }

  const handleKeyDown = event => {
    if (event.key === 'Enter' || event.key === 'NumpadEnter') {
      handleSubmit(onSubmitHandler)()
    }
  }

  useEffect(() => {
    if (isOpen) {
      reset({ groupName: get(existingGroup, 'groupName', '') })
    }
  }, [isOpen])

  return (
    <HHBaseDialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth>
      <HHDialogTitle title={existingGroup ? T.EDIT_INVOICE_GROUP : T.NEW_INVOICE_GROUP} onClose={onClose} />
      <DialogContent>
        <FormProvider {...invoiceGroupForm}>
          <Box mt={2}>
            <HHFormTextField
              autoFocus
              error={groupNameHelperText}
              helperText={groupNameHelperText}
              control={control}
              rules={{ required: T.CANNOT_BE_EMPTY }}
              name="groupName"
              label={T.INVOICE_GROUP_NAME}
              placeholder={T.INVOICE_GROUP_NAME}
              fullWidth
              deprecatedLabel={false}
              onKeyDown={handleKeyDown}
            />
          </Box>
        </FormProvider>
      </DialogContent>
      <HHDialogActions>
        <CancelButton size="medium" onClick={onClose} />
        <Button loading={isCreateLoading || isUpdateLoading} size="small" variant="contained" onClick={handleSubmit(onSubmitHandler)}>
          {existingGroup ? T.SAVE : T.CREATE}
        </Button>
      </HHDialogActions>
    </HHBaseDialog>
  )
}

AddEditInvoiceGroupDialog.propTypes = {
  isOpen: PropTypes.bool,
  existingGroup: PropTypes.string,
  onSuccess: PropTypes.func,
  onClose: PropTypes.func.isRequired,
}

export default AddEditInvoiceGroupDialog
